import { ApiClientError, type ApiErrorPayload, type ApiFieldErrorValue } from '@/lib/api';

export type ApiErrorTranslationKey =
    | 'apiErrors.auth.required'
    | 'apiErrors.auth.invalidOrExpiredToken'
    | 'apiErrors.validation.invalidRequestBody'
    | 'apiErrors.validation.required'
    | 'apiErrors.validation.emailInvalid'
    | 'apiErrors.validation.stringMaxLength'
    | 'apiErrors.organizations.invalidRequest'
    | 'apiErrors.organizations.conflict'
    | 'apiErrors.organizations.notFound'
    | 'apiErrors.organizations.loadFailed'
    | 'apiErrors.organizations.createFailed'
    | 'apiErrors.organizations.validation.nameMaxLength'
    | 'apiErrors.organizations.validation.slugMaxLength'
    | 'apiErrors.organizations.validation.slugFormat'
    | 'apiErrors.onboarding.updateForbidden'
    | 'apiErrors.onboarding.validation.goalsRequiredBeforeConnect'
    | 'apiErrors.billing.invalidRequest'
    | 'apiErrors.billing.manageForbidden'
    | 'apiErrors.billing.organizationNotFound'
    | 'apiErrors.billing.organizationOrProductNotFound'
    | 'apiErrors.billing.incompleteConfiguration'
    | 'apiErrors.billing.lifetimeSubscriptionActive'
    | 'apiErrors.billing.integration'
    | 'apiErrors.billing.productsLoadFailed'
    | 'apiErrors.billing.stateLoadFailed'
    | 'apiErrors.billing.checkoutFailed'
    | 'apiErrors.billing.customerPortalFailed'
    | 'apiErrors.billing.validation.successUrlInvalid'
    | 'apiErrors.billing.validation.cancelUrlInvalid'
    | 'apiErrors.billing.validation.returnUrlInvalid'
    | 'apiErrors.userProfile.loadFailed'
    | 'apiErrors.userProfile.updateFailed'
    | 'apiErrors.userProfile.actionEmailFailed'
    | 'apiErrors.userProfile.accountServiceUnavailable'
    | 'apiErrors.userProfile.notFound'
    | 'apiErrors.userProfile.updateRejected'
    | 'apiErrors.userProfile.actionRejected'
    | 'apiErrors.userProfile.validation.readOnly'
    | 'apiErrors.userProfile.validation.unsupported'
    | 'apiErrors.organizationInvitations.listForbidden'
    | 'apiErrors.organizationInvitations.createForbidden'
    | 'apiErrors.organizationInvitations.revokeForbidden'
    | 'apiErrors.organizationInvitations.createConflict'
    | 'apiErrors.organizationInvitations.revokeConflict'
    | 'apiErrors.organizationInvitations.notFound'
    | 'apiErrors.organizationInvitations.invalidToken'
    | 'apiErrors.organizationInvitations.actionFailed'
    | 'apiErrors.organizationMembers.listForbidden'
    | 'apiErrors.organizationMembers.removeForbidden'
    | 'apiErrors.organizationMembers.validation.roleInvalid'
    | 'apiErrors.organizationMembers.removeConflict'
    | 'apiErrors.organizationMembers.updateForbidden'
    | 'apiErrors.organizationMembers.updateConflict'
    | 'apiErrors.organizationMembers.actionFailed'
    | 'apiErrors.organizationApiKeys.listForbidden'
    | 'apiErrors.organizationApiKeys.manageForbidden'
    | 'apiErrors.organizationApiKeys.validation.nameMaxLength'
    | 'apiErrors.organizationApiKeys.notFound'
    | 'apiErrors.organizationApiKeys.createConflict'
    | 'apiErrors.organizationApiKeys.revokeConflict'
    | 'apiErrors.organizationApiKeys.actionFailed'
    | 'apiErrors.socialAccounts.listFailed'
    | 'apiErrors.socialAccounts.connectFailed'
    | 'apiErrors.socialAccounts.reconnectFailed'
    | 'apiErrors.socialAccounts.disconnectFailed'
    | 'apiErrors.socialAccounts.forbidden'
    | 'apiErrors.socialAccounts.notFound'
    | 'apiErrors.socialAccounts.conflict'
    | 'apiErrors.publishing.listFailed'
    | 'apiErrors.publishing.detailFailed'
    | 'apiErrors.publishing.createFailed'
    | 'apiErrors.publishing.paidPlanLimitExceeded'
    | 'apiErrors.publishing.paidPlanLimitExceededForAccount'
    | 'apiErrors.publishing.paidPlanLimitExceededUnknownAccount'
    | 'apiErrors.publishing.paidPlanLimitExceededUsageDetails'
    | 'apiErrors.publishing.paidPlanLimitExceededResetDetails'
    | 'apiErrors.publishing.forbidden'
    | 'apiErrors.publishing.notFound'
    | 'apiErrors.publishing.conflict'
    | 'apiErrors.publishing.validation.mediaUrlUnreachable'
    | 'apiErrors.publishing.validation.mediaUrlHttpError'
    | 'apiErrors.publishing.validation.mediaUrlNotImage'
    | 'apiErrors.publishing.validation.mediaUrlNotVideo'
    | 'apiErrors.oauthConsent.loadFailed'
    | 'apiErrors.oauthConsent.approveFailed'
    | 'apiErrors.oauthConsent.denyFailed'
    | 'apiErrors.oauthConsent.invalidRequest'
    | 'apiErrors.oauthConsent.forbidden'
    | 'apiErrors.oauthConsent.notFound'
    | 'apiErrors.insights.loadFailed'
    | 'apiErrors.insights.invalidQuery'
    | 'apiErrors.insights.forbidden'
    | 'apiErrors.insights.notFound'
    | 'apiErrors.insights.notReadable'
    | 'apiErrors.insights.providerRejected';

export type ApiErrorStatusFallbacks = Partial<Record<number, ApiErrorTranslationKey>>;

const backendTranslationKeyMap: Record<string, ApiErrorTranslationKey> = {
    paid_plan_limit_exceeded: 'apiErrors.publishing.paidPlanLimitExceeded',
    'validation.required': 'apiErrors.validation.required',
    'validation.email.invalid': 'apiErrors.validation.emailInvalid',
    'validation.string.max_length': 'apiErrors.validation.stringMaxLength',
    'organizations.validation.name.max_length': 'apiErrors.organizations.validation.nameMaxLength',
    'organizations.validation.slug.max_length': 'apiErrors.organizations.validation.slugMaxLength',
    'organizations.validation.slug_format': 'apiErrors.organizations.validation.slugFormat',
    'onboarding.validation.goals.required_before_connect': 'apiErrors.onboarding.validation.goalsRequiredBeforeConnect',
    'publishing.validation.media_items.media_url_unreachable': 'apiErrors.publishing.validation.mediaUrlUnreachable',
    'publishing.validation.media_items.media_url_http_error': 'apiErrors.publishing.validation.mediaUrlHttpError',
    'publishing.validation.media_items.media_url_not_image': 'apiErrors.publishing.validation.mediaUrlNotImage',
    'publishing.validation.media_items.media_url_not_video': 'apiErrors.publishing.validation.mediaUrlNotVideo',
    'memberships.validation.role.invalid': 'apiErrors.organizationMembers.validation.roleInvalid',
    'api_keys.validation.name.max_length': 'apiErrors.organizationApiKeys.validation.nameMaxLength',
    'billing.validation.success_url.invalid': 'apiErrors.billing.validation.successUrlInvalid',
    'billing.validation.cancel_url.invalid': 'apiErrors.billing.validation.cancelUrlInvalid',
    'billing.validation.return_url.invalid': 'apiErrors.billing.validation.returnUrlInvalid',
    'openapi.operations.organizationBilling.current.responses.403.description': 'apiErrors.billing.manageForbidden',
    'openapi.operations.organizationBilling.current.responses.404.description':
        'apiErrors.billing.organizationNotFound',
    'openapi.operations.organizationBilling.current.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationBilling.checkoutSession.create.responses.400.description':
        'apiErrors.billing.invalidRequest',
    'openapi.operations.organizationBilling.checkoutSession.create.responses.403.description':
        'apiErrors.billing.manageForbidden',
    'openapi.operations.organizationBilling.checkoutSession.create.responses.404.description':
        'apiErrors.billing.organizationOrProductNotFound',
    'openapi.operations.organizationBilling.checkoutSession.create.responses.409.description':
        'apiErrors.billing.incompleteConfiguration',
    'openapi.operations.organizationBilling.checkoutSession.create.responses.422.description':
        'apiErrors.billing.integration',
    'openapi.operations.organizationBilling.checkoutSession.create.responses.401.description':
        'apiErrors.auth.required',
    'openapi.operations.organizationBilling.customerPortalSession.create.responses.400.description':
        'apiErrors.billing.invalidRequest',
    'openapi.operations.organizationBilling.customerPortalSession.create.responses.403.description':
        'apiErrors.billing.manageForbidden',
    'openapi.operations.organizationBilling.customerPortalSession.create.responses.404.description':
        'apiErrors.billing.organizationNotFound',
    'openapi.operations.organizationBilling.customerPortalSession.create.responses.422.description':
        'apiErrors.billing.integration',
    'openapi.operations.organizationBilling.customerPortalSession.create.responses.401.description':
        'apiErrors.auth.required',
    'openapi.operations.userProfile.show.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.userProfile.show.responses.502.description': 'apiErrors.userProfile.accountServiceUnavailable',
    'openapi.operations.userProfile.update.responses.400.description': 'apiErrors.validation.invalidRequestBody',
    'openapi.operations.userProfile.update.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.userProfile.update.responses.422.description': 'apiErrors.userProfile.updateRejected',
    'openapi.operations.userProfile.update.responses.502.description':
        'apiErrors.userProfile.accountServiceUnavailable',
    'openapi.operations.userProfile.passwordUpdateEmail.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.userProfile.passwordUpdateEmail.responses.502.description':
        'apiErrors.userProfile.accountServiceUnavailable',
    'openapi.operations.userProfile.twoFactorSetupEmail.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.userProfile.twoFactorSetupEmail.responses.502.description':
        'apiErrors.userProfile.accountServiceUnavailable',
    'openapi.operations.auth.me.responses.401.description': 'apiErrors.auth.invalidOrExpiredToken',
    'openapi.operations.organizations.list.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizations.create.responses.400.description': 'apiErrors.organizations.invalidRequest',
    'openapi.operations.organizations.create.responses.409.description': 'apiErrors.organizations.conflict',
    'openapi.operations.organizations.create.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationInvitations.list.responses.403.description':
        'apiErrors.organizationInvitations.listForbidden',
    'openapi.operations.organizationInvitations.list.responses.404.description': 'apiErrors.organizations.notFound',
    'openapi.operations.organizationInvitations.list.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationInvitations.create.responses.400.description':
        'apiErrors.validation.invalidRequestBody',
    'openapi.operations.organizationInvitations.create.responses.403.description':
        'apiErrors.organizationInvitations.createForbidden',
    'openapi.operations.organizationInvitations.create.responses.404.description': 'apiErrors.organizations.notFound',
    'openapi.operations.organizationInvitations.create.responses.409.description':
        'apiErrors.organizationInvitations.createConflict',
    'openapi.operations.organizationInvitations.create.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationInvitations.revoke.responses.403.description':
        'apiErrors.organizationInvitations.revokeForbidden',
    'openapi.operations.organizationInvitations.revoke.responses.404.description':
        'apiErrors.organizationInvitations.notFound',
    'openapi.operations.organizationInvitations.revoke.responses.409.description':
        'apiErrors.organizationInvitations.revokeConflict',
    'openapi.operations.organizationInvitations.revoke.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationInvitations.resolve.responses.409.description':
        'apiErrors.organizationInvitations.invalidToken',
    'openapi.operations.organizationInvitations.accept.responses.409.description':
        'apiErrors.organizationInvitations.invalidToken',
    'openapi.operations.organizationInvitations.accept.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationMembers.list.responses.403.description':
        'apiErrors.organizationMembers.listForbidden',
    'openapi.operations.organizationMembers.list.responses.404.description': 'apiErrors.organizations.notFound',
    'openapi.operations.organizationMembers.list.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationMembers.remove.responses.403.description':
        'apiErrors.organizationMembers.removeForbidden',
    'openapi.operations.organizationMembers.remove.responses.404.description': 'apiErrors.organizations.notFound',
    'openapi.operations.organizationMembers.remove.responses.409.description':
        'apiErrors.organizationMembers.removeConflict',
    'openapi.operations.organizationMembers.remove.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationMembers.update.responses.400.description':
        'apiErrors.validation.invalidRequestBody',
    'openapi.operations.organizationMembers.update.responses.403.description':
        'apiErrors.organizationMembers.updateForbidden',
    'openapi.operations.organizationMembers.update.responses.404.description': 'apiErrors.organizations.notFound',
    'openapi.operations.organizationMembers.update.responses.409.description':
        'apiErrors.organizationMembers.updateConflict',
    'openapi.operations.organizationMembers.update.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationApiKeys.list.responses.403.description':
        'apiErrors.organizationApiKeys.listForbidden',
    'openapi.operations.organizationApiKeys.list.responses.404.description': 'apiErrors.organizations.notFound',
    'openapi.operations.organizationApiKeys.list.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationApiKeys.create.responses.400.description':
        'apiErrors.validation.invalidRequestBody',
    'openapi.operations.organizationApiKeys.create.responses.403.description':
        'apiErrors.organizationApiKeys.manageForbidden',
    'openapi.operations.organizationApiKeys.create.responses.404.description': 'apiErrors.organizations.notFound',
    'openapi.operations.organizationApiKeys.create.responses.409.description':
        'apiErrors.organizationApiKeys.createConflict',
    'openapi.operations.organizationApiKeys.create.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.organizationApiKeys.revoke.responses.403.description':
        'apiErrors.organizationApiKeys.manageForbidden',
    'openapi.operations.organizationApiKeys.revoke.responses.404.description': 'apiErrors.organizationApiKeys.notFound',
    'openapi.operations.organizationApiKeys.revoke.responses.409.description':
        'apiErrors.organizationApiKeys.revokeConflict',
    'openapi.operations.organizationApiKeys.revoke.responses.401.description': 'apiErrors.auth.required',
    'openapi.operations.socialAccounts.connect.responses.400.description': 'apiErrors.validation.invalidRequestBody',
    'openapi.operations.socialAccounts.connect.responses.403.description': 'apiErrors.socialAccounts.forbidden',
    'openapi.operations.socialAccounts.connect.responses.404.description': 'apiErrors.socialAccounts.notFound',
    'openapi.operations.socialAccounts.list.responses.403.description': 'apiErrors.socialAccounts.forbidden',
    'openapi.operations.socialAccounts.list.responses.404.description': 'apiErrors.socialAccounts.notFound',
    'openapi.operations.socialAccounts.disconnect.responses.403.description': 'apiErrors.socialAccounts.forbidden',
    'openapi.operations.socialAccounts.disconnect.responses.404.description': 'apiErrors.socialAccounts.notFound',
    'openapi.operations.socialAccounts.disconnect.responses.409.description': 'apiErrors.socialAccounts.conflict',
    'openapi.operations.socialAccounts.reconnect.responses.400.description': 'apiErrors.validation.invalidRequestBody',
    'openapi.operations.socialAccounts.reconnect.responses.403.description': 'apiErrors.socialAccounts.forbidden',
    'openapi.operations.socialAccounts.reconnect.responses.404.description': 'apiErrors.socialAccounts.notFound',
    'openapi.operations.socialAccounts.reconnect.responses.409.description': 'apiErrors.socialAccounts.conflict',
    'openapi.operations.insights.instagramAccount.get.responses.400.description': 'apiErrors.insights.invalidQuery',
    'openapi.operations.insights.instagramAccount.get.responses.403.description': 'apiErrors.insights.forbidden',
    'openapi.operations.insights.instagramAccount.get.responses.404.description': 'apiErrors.insights.notFound',
    'openapi.operations.insights.instagramAccount.get.responses.409.description': 'apiErrors.insights.notReadable',
    'insights.validation.account.provider_unavailable': 'apiErrors.insights.providerRejected',
    'insights.validation.account.not_readable': 'apiErrors.insights.notReadable',
    'insights.validation.account.scopes_missing': 'apiErrors.insights.notReadable',
    'insights.validation.account.authorization_missing': 'apiErrors.insights.notReadable',
    'insights.validation.account.credential_missing': 'apiErrors.insights.notReadable',
    'insights.validation.account.access_token_invalid': 'apiErrors.insights.notReadable',
    'insights.validation.account.login_strategy_unsupported': 'apiErrors.insights.notReadable',
    'insights.validation.account.provider_unsupported': 'apiErrors.insights.invalidQuery',
    'insights.validation.account.target_type_unsupported': 'apiErrors.insights.invalidQuery',
    'insights.validation.query.date_invalid': 'apiErrors.insights.invalidQuery'
};

const exactMessageKeyMap: Record<string, ApiErrorTranslationKey> = {
    'Authentication required.': 'apiErrors.auth.required',
    'Authentication required or token invalid.': 'apiErrors.auth.invalidOrExpiredToken',
    'Invalid request.': 'apiErrors.organizations.invalidRequest',
    'Invalid request body or validation error.': 'apiErrors.validation.invalidRequestBody',
    'Organization conflict.': 'apiErrors.organizations.conflict',
    'Organization not found.': 'apiErrors.organizations.notFound',
    'Invalid or missing request parameters.': 'apiErrors.billing.invalidRequest',
    'Authenticated user is not allowed to manage organization billing.': 'apiErrors.billing.manageForbidden',
    'Authenticated user is not allowed to update organization onboarding state.':
        'apiErrors.onboarding.updateForbidden',
    'Organization or product not found.': 'apiErrors.billing.organizationOrProductNotFound',
    'Billing configuration is incomplete for the requested checkout session.':
        'apiErrors.billing.incompleteConfiguration',
    'Organization has a lifetime subscription. Revoke it with billing:lifetime:revoke before using Stripe billing.':
        'apiErrors.billing.lifetimeSubscriptionActive',
    'Stripe integration error.': 'apiErrors.billing.integration',
    'Keycloak request failed.': 'apiErrors.userProfile.accountServiceUnavailable',
    'Unable to complete the Keycloak user profile request.': 'apiErrors.userProfile.accountServiceUnavailable',
    'This field is read-only.': 'apiErrors.userProfile.validation.readOnly',
    'This field is not supported.': 'apiErrors.userProfile.validation.unsupported',
    'Authenticated user is not allowed to list invitations.': 'apiErrors.organizationInvitations.listForbidden',
    'Authenticated user is not allowed to invite members.': 'apiErrors.organizationInvitations.createForbidden',
    'Authenticated user is not allowed to revoke invitations.': 'apiErrors.organizationInvitations.revokeForbidden',
    'Invitation creation violates organization rules.': 'apiErrors.organizationInvitations.createConflict',
    'Invitation revocation violates invitation state rules.': 'apiErrors.organizationInvitations.revokeConflict',
    'Organization or invitation not found.': 'apiErrors.organizationInvitations.notFound',
    'Invitation token is invalid or cannot be accepted anymore.': 'apiErrors.organizationInvitations.invalidToken',
    'Invitation token is invalid or cannot be accepted.': 'apiErrors.organizationInvitations.invalidToken',
    'Authenticated user is not allowed to list organization members.': 'apiErrors.organizationMembers.listForbidden',
    'Authenticated user is not allowed to remove this membership.': 'apiErrors.organizationMembers.removeForbidden',
    'Membership removal violates organization rules.': 'apiErrors.organizationMembers.removeConflict',
    'Authenticated user is not allowed to update this membership.': 'apiErrors.organizationMembers.updateForbidden',
    'Membership update violates organization rules.': 'apiErrors.organizationMembers.updateConflict',
    'Authenticated user is not allowed to list API keys.': 'apiErrors.organizationApiKeys.listForbidden',
    'Authenticated user is not allowed to manage API keys.': 'apiErrors.organizationApiKeys.manageForbidden',
    'Organization or API key not found.': 'apiErrors.organizationApiKeys.notFound',
    'API key creation violates organization rules.': 'apiErrors.organizationApiKeys.createConflict',
    'API key revocation violates API key state rules.': 'apiErrors.organizationApiKeys.revokeConflict',
    'Authenticated user is not allowed to manage social accounts for this organization.':
        'apiErrors.socialAccounts.forbidden',
    'Organization, social account, or authorization not found.': 'apiErrors.socialAccounts.notFound',
    'Authorization could not be disconnected.': 'apiErrors.socialAccounts.conflict',
    'Authorization could not be reconnected safely.': 'apiErrors.socialAccounts.conflict',
    'Organization API key is not allowed to list publish jobs for the organization.': 'apiErrors.publishing.forbidden',
    'Organization API key is not allowed to view publish jobs for the organization.': 'apiErrors.publishing.forbidden',
    'Organization API key is not allowed to create publish jobs for the organization.':
        'apiErrors.publishing.forbidden',
    'Organization or publish job not found.': 'apiErrors.publishing.notFound',
    'Publish job request violates publishing constraints or idempotency rules.': 'apiErrors.publishing.conflict',
    'Missing or invalid query parameters.': 'apiErrors.insights.invalidQuery',
    'Authenticated user is not allowed to view insights for this organization.': 'apiErrors.insights.forbidden',
    'Organization or social account not found.': 'apiErrors.insights.notFound',
    'Connected account is not currently readable for insights.': 'apiErrors.insights.notReadable',
    'No organization selected': 'apiErrors.organizations.notFound'
};

const normalizedMessageKeyMap = new Map<string, ApiErrorTranslationKey>(
    Object.entries(exactMessageKeyMap).map(([message, key]) => [normalizeBackendMessage(message), key])
);

export const apiErrorStatusFallbackPresets = {
    organizationsLoad: {
        401: 'apiErrors.auth.required'
    },
    organizationsCreate: {
        400: 'apiErrors.organizations.invalidRequest',
        401: 'apiErrors.auth.required',
        409: 'apiErrors.organizations.conflict'
    },
    authMe: {
        401: 'apiErrors.auth.invalidOrExpiredToken'
    },
    billingProductsLoad: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.billing.manageForbidden',
        404: 'apiErrors.billing.organizationNotFound'
    },
    billingStateLoad: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.billing.manageForbidden',
        404: 'apiErrors.billing.organizationNotFound'
    },
    billingCheckout: {
        400: 'apiErrors.billing.invalidRequest',
        401: 'apiErrors.auth.required',
        403: 'apiErrors.billing.manageForbidden',
        404: 'apiErrors.billing.organizationOrProductNotFound',
        409: 'apiErrors.billing.incompleteConfiguration',
        422: 'apiErrors.billing.integration'
    },
    billingCustomerPortal: {
        400: 'apiErrors.billing.invalidRequest',
        401: 'apiErrors.auth.required',
        403: 'apiErrors.billing.manageForbidden',
        404: 'apiErrors.billing.organizationNotFound',
        409: 'apiErrors.billing.incompleteConfiguration',
        422: 'apiErrors.billing.integration'
    },
    userProfileLoad: {
        401: 'apiErrors.auth.required',
        404: 'apiErrors.userProfile.notFound',
        502: 'apiErrors.userProfile.accountServiceUnavailable'
    },
    userProfileUpdate: {
        400: 'apiErrors.validation.invalidRequestBody',
        401: 'apiErrors.auth.required',
        404: 'apiErrors.userProfile.notFound',
        409: 'apiErrors.userProfile.actionRejected',
        422: 'apiErrors.userProfile.updateRejected',
        502: 'apiErrors.userProfile.accountServiceUnavailable'
    },
    userProfileActionEmail: {
        401: 'apiErrors.auth.required',
        404: 'apiErrors.userProfile.notFound',
        409: 'apiErrors.userProfile.actionRejected',
        422: 'apiErrors.userProfile.actionRejected',
        502: 'apiErrors.userProfile.accountServiceUnavailable'
    },
    onboardingStateUpdate: {
        400: 'apiErrors.validation.invalidRequestBody',
        401: 'apiErrors.auth.required',
        403: 'apiErrors.onboarding.updateForbidden',
        422: 'apiErrors.onboarding.validation.goalsRequiredBeforeConnect'
    },
    organizationInvitationsList: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.organizationInvitations.listForbidden',
        404: 'apiErrors.organizations.notFound'
    },
    organizationInvitationsCreate: {
        400: 'apiErrors.validation.invalidRequestBody',
        401: 'apiErrors.auth.required',
        403: 'apiErrors.organizationInvitations.createForbidden',
        404: 'apiErrors.organizations.notFound',
        409: 'apiErrors.organizationInvitations.createConflict'
    },
    organizationInvitationsRevoke: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.organizationInvitations.revokeForbidden',
        404: 'apiErrors.organizationInvitations.notFound',
        409: 'apiErrors.organizationInvitations.revokeConflict'
    },
    organizationInvitationAccept: {
        401: 'apiErrors.auth.required',
        409: 'apiErrors.organizationInvitations.invalidToken'
    },
    organizationInvitationDecline: {
        401: 'apiErrors.auth.required',
        409: 'apiErrors.organizationInvitations.invalidToken'
    },
    organizationInvitationResolve: {
        409: 'apiErrors.organizationInvitations.invalidToken'
    },
    organizationMembersList: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.organizationMembers.listForbidden',
        404: 'apiErrors.organizations.notFound'
    },
    organizationMembersUpdate: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.organizationMembers.updateForbidden',
        404: 'apiErrors.organizations.notFound',
        409: 'apiErrors.organizationMembers.updateConflict'
    },
    organizationMembersRemove: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.organizationMembers.removeForbidden',
        404: 'apiErrors.organizations.notFound',
        409: 'apiErrors.organizationMembers.removeConflict'
    },
    organizationApiKeysList: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.organizationApiKeys.listForbidden',
        404: 'apiErrors.organizations.notFound'
    },
    organizationApiKeysCreate: {
        400: 'apiErrors.validation.invalidRequestBody',
        401: 'apiErrors.auth.required',
        403: 'apiErrors.organizationApiKeys.manageForbidden',
        404: 'apiErrors.organizations.notFound',
        409: 'apiErrors.organizationApiKeys.createConflict'
    },
    organizationApiKeysRevoke: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.organizationApiKeys.manageForbidden',
        404: 'apiErrors.organizationApiKeys.notFound',
        409: 'apiErrors.organizationApiKeys.revokeConflict'
    },
    organizationApiKeysDetails: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.organizationApiKeys.manageForbidden',
        404: 'apiErrors.organizationApiKeys.notFound'
    },
    socialAccountsList: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.socialAccounts.forbidden',
        404: 'apiErrors.socialAccounts.notFound'
    },
    socialAccountsConnect: {
        400: 'apiErrors.validation.invalidRequestBody',
        401: 'apiErrors.auth.required',
        403: 'apiErrors.socialAccounts.forbidden',
        404: 'apiErrors.socialAccounts.notFound'
    },
    socialAccountsReconnect: {
        400: 'apiErrors.validation.invalidRequestBody',
        401: 'apiErrors.auth.required',
        403: 'apiErrors.socialAccounts.forbidden',
        404: 'apiErrors.socialAccounts.notFound',
        409: 'apiErrors.socialAccounts.conflict'
    },
    socialAccountsDisconnect: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.socialAccounts.forbidden',
        404: 'apiErrors.socialAccounts.notFound',
        409: 'apiErrors.socialAccounts.conflict'
    },
    publishingList: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.publishing.forbidden',
        404: 'apiErrors.publishing.notFound'
    },
    publishingDetail: {
        401: 'apiErrors.auth.required',
        403: 'apiErrors.publishing.forbidden',
        404: 'apiErrors.publishing.notFound'
    },
    publishingCreate: {
        400: 'apiErrors.validation.invalidRequestBody',
        401: 'apiErrors.auth.required',
        403: 'apiErrors.publishing.forbidden',
        404: 'apiErrors.publishing.notFound',
        409: 'apiErrors.publishing.conflict'
    },
    oauthConsentLoad: {
        401: 'apiErrors.auth.required',
        404: 'apiErrors.oauthConsent.notFound'
    },
    oauthConsentApprove: {
        400: 'apiErrors.oauthConsent.invalidRequest',
        401: 'apiErrors.auth.required',
        403: 'apiErrors.oauthConsent.forbidden',
        404: 'apiErrors.oauthConsent.notFound'
    },
    oauthConsentDeny: {
        401: 'apiErrors.auth.required',
        404: 'apiErrors.oauthConsent.notFound'
    },
    insightsLoad: {
        400: 'apiErrors.insights.invalidQuery',
        401: 'apiErrors.auth.required',
        403: 'apiErrors.insights.forbidden',
        404: 'apiErrors.insights.notFound',
        409: 'apiErrors.insights.notReadable'
    }
} satisfies Record<string, ApiErrorStatusFallbacks>;

type TranslationFunction = (key: string, options?: Record<string, unknown>) => string;

type PublishingLimitSocialAccount = {
    id: string;
    displayName?: string | null;
    handleOrUsername?: string | null;
    provider?: string | null;
};

const PAID_PLAN_LIMIT_EXCEEDED_CODE = 'paid_plan_limit_exceeded';

function getPayloadStringValue(payload: ApiErrorPayload | undefined, field: string): string | null {
    const value = payload?.[field];

    return typeof value === 'string' && value.length > 0 ? value : null;
}

function getErrorMetaStringValue(error: ApiClientError, field: string): string | null {
    const value = error.errorMeta?.[field];

    return typeof value === 'string' && value.length > 0 ? value : null;
}

function getErrorMetaNumberValue(error: ApiClientError, field: string): number | null {
    const value = getErrorMetaStringValue(error, field);

    if (value === null) {
        return null;
    }

    const parsedValue = Number(value);

    return Number.isFinite(parsedValue) ? parsedValue : null;
}

function formatProviderLabel(provider: string | null): string | null {
    if (provider === null) {
        return null;
    }

    if (provider.toLowerCase() === 'x') {
        return 'X';
    }

    return provider.charAt(0).toUpperCase() + provider.slice(1);
}

function formatSocialAccountLabel(
    socialAccount: PublishingLimitSocialAccount | undefined,
    provider: string | null,
    t: TranslationFunction
): string {
    if (socialAccount) {
        const handle = socialAccount.handleOrUsername ? ` @${socialAccount.handleOrUsername}` : '';
        const providerLabel = formatProviderLabel(provider ?? socialAccount.provider ?? null);
        const providerSuffix = providerLabel ? ` (${providerLabel})` : '';

        return `${socialAccount.displayName ?? providerLabel ?? t('apiErrors.publishing.paidPlanLimitExceededUnknownAccount')}${handle}${providerSuffix}`;
    }

    const providerLabel = formatProviderLabel(provider);

    if (providerLabel) {
        return `${providerLabel} account`;
    }

    return t('apiErrors.publishing.paidPlanLimitExceededUnknownAccount');
}

function formatResetAt(resetAt: string | null, locale?: string): string | null {
    if (resetAt === null) {
        return null;
    }

    const parsedResetAt = new Date(resetAt);

    if (Number.isNaN(parsedResetAt.getTime())) {
        return resetAt;
    }

    return new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short'
    }).format(parsedResetAt);
}

export function isPaidPlanLimitExceededApiError(error: unknown): boolean {
    if (!(error instanceof ApiClientError)) {
        return false;
    }

    return (
        getPayloadStringValue(error.payload, 'code') === PAID_PLAN_LIMIT_EXCEEDED_CODE ||
        getPayloadStringValue(error.payload, 'error') === PAID_PLAN_LIMIT_EXCEEDED_CODE ||
        getErrorMetaStringValue(error, 'targets') === PAID_PLAN_LIMIT_EXCEEDED_CODE ||
        Object.values(error.errorMeta ?? {}).includes(PAID_PLAN_LIMIT_EXCEEDED_CODE)
    );
}

export function resolvePaidPlanLimitExceededApiErrorMessage(
    error: unknown,
    t: TranslationFunction,
    options: {
        socialAccounts?: PublishingLimitSocialAccount[];
        locale?: string;
    } = {}
): string | null {
    if (!(error instanceof ApiClientError) || !isPaidPlanLimitExceededApiError(error)) {
        return null;
    }

    const socialAccountId = getErrorMetaStringValue(error, 'socialAccountId');
    const provider = getErrorMetaStringValue(error, 'provider');
    const socialAccount = options.socialAccounts?.find((account) => account.id === socialAccountId);
    const limit = getErrorMetaNumberValue(error, 'limit');
    const currentUsage = getErrorMetaNumberValue(error, 'currentUsage');
    const activeReservations = getErrorMetaNumberValue(error, 'activeReservations');
    const requestedCount = getErrorMetaNumberValue(error, 'requestedCount');
    const resetAt = formatResetAt(getErrorMetaStringValue(error, 'resetAt'), options.locale);
    const details: string[] = [];

    if (limit !== null || currentUsage !== null || activeReservations !== null || requestedCount !== null) {
        details.push(
            t('apiErrors.publishing.paidPlanLimitExceededUsageDetails', {
                limit: limit ?? '-',
                currentUsage: currentUsage ?? '-',
                activeReservations: activeReservations ?? '-',
                requestedCount: requestedCount ?? '-'
            })
        );
    }

    if (resetAt !== null) {
        details.push(t('apiErrors.publishing.paidPlanLimitExceededResetDetails', { resetAt }));
    }

    return [
        t('apiErrors.publishing.paidPlanLimitExceededForAccount', {
            account: formatSocialAccountLabel(socialAccount, provider, t)
        }),
        ...details
    ].join(' ');
}

export function normalizeBackendMessage(message: string): string {
    return message
        .trim()
        .toLowerCase()
        .replace(/[.!?]+$/g, '')
        .replace(/\s+/g, ' ');
}

function getPayloadTranslationKeyCandidates(payload?: ApiErrorPayload): string[] {
    if (!payload) {
        return [];
    }

    return [
        payload.translationKey,
        payload.translationKeys?.description,
        ...Object.values(payload.errorMeta ?? {})
    ].filter((value): value is string => typeof value === 'string' && value.length > 0);
}

function getPayloadMessageCandidates(payload?: ApiErrorPayload): string[] {
    if (!payload) {
        return [];
    }

    return [payload.description, payload.message, payload.error].filter(
        (value): value is string => typeof value === 'string' && value.length > 0
    );
}

const getPayloadFieldErrorCandidates = (payload?: ApiErrorPayload): string[] => {
    if (!payload?.errors) {
        return [];
    }

    return Object.values(payload.errors).flatMap((value) => normalizeApiFieldErrorValue(value));
};

export function getApiErrorTranslationKey(error: unknown): ApiErrorTranslationKey | null {
    if (!(error instanceof ApiClientError)) {
        return null;
    }

    for (const candidate of getPayloadTranslationKeyCandidates(error.payload)) {
        const backendTranslationKey = backendTranslationKeyMap[candidate];
        if (backendTranslationKey) {
            return backendTranslationKey;
        }
    }

    const messageCandidates = [
        ...getPayloadMessageCandidates(error.payload),
        ...getPayloadFieldErrorCandidates(error.payload)
    ];

    for (const candidate of messageCandidates) {
        const exactMatch = exactMessageKeyMap[candidate];
        if (exactMatch) {
            return exactMatch;
        }

        const normalizedMatch = normalizedMessageKeyMap.get(normalizeBackendMessage(candidate));
        if (normalizedMatch) {
            return normalizedMatch;
        }
    }

    return null;
}

export function normalizeApiFieldErrorValue(value: ApiFieldErrorValue | undefined): string[] {
    if (typeof value === 'string') {
        return value ? [value] : [];
    }

    if (Array.isArray(value)) {
        return value.filter((message): message is string => typeof message === 'string' && message.length > 0);
    }

    return [];
}

/**
 * Backend violation paths use bracket notation for collections (`mediaItems[0].sourceUrl`)
 * while forms look fields up in dot notation (`mediaItems.0.sourceUrl`).
 */
export function normalizeApiFieldErrorKey(field: string): string {
    return field.replace(/\[(\d+)\]/g, '.$1');
}

export function getApiFieldErrors(error: unknown): Record<string, string[]> {
    if (!(error instanceof ApiClientError)) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(error.errors ?? {}).map(([field, value]) => [
            normalizeApiFieldErrorKey(field),
            normalizeApiFieldErrorValue(value)
        ])
    );
}

/**
 * Like getApiFieldErrors, but replaces messages with their locale translation whenever the
 * field's errorMeta key maps to a known translation key, so backend field errors follow the
 * active language instead of always showing the backend's English text.
 */
export function getTranslatedApiFieldErrors(
    error: unknown,
    t: (key: string, options?: Record<string, unknown>) => string
): Record<string, string[]> {
    if (!(error instanceof ApiClientError)) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(error.errors ?? {}).map(([field, value]) => {
            const translationKey = getApiFieldTranslationKey(error, field);
            const messages = normalizeApiFieldErrorValue(value);

            // errorMeta carries a single code per field describing its primary
            // violation, so the translation replaces the first message only;
            // any further violations stay visible untranslated.
            return [
                normalizeApiFieldErrorKey(field),
                translationKey ? [t(translationKey), ...messages.slice(1)] : messages
            ];
        })
    );
}

export function getApiFieldTranslationKey(error: unknown, field: string): ApiErrorTranslationKey | null {
    if (!(error instanceof ApiClientError)) {
        return null;
    }

    const candidate = error.errorMeta?.[field];
    if (!candidate) {
        return null;
    }

    return backendTranslationKeyMap[candidate] ?? null;
}

export function getApiFieldError(error: unknown, field: string): string | null {
    return getApiFieldErrors(error)[field]?.[0] ?? null;
}

export function getTranslatedApiFieldError(
    error: unknown,
    field: string,
    t: (key: string, options?: Record<string, unknown>) => string
): string | null {
    const translationKey = getApiFieldTranslationKey(error, field);
    if (translationKey) {
        return t(translationKey);
    }

    const fieldError = getApiFieldError(error, field);
    if (!fieldError) {
        return null;
    }

    const exactMatch = exactMessageKeyMap[fieldError];
    if (exactMatch) {
        return t(exactMatch);
    }

    const normalizedMatch = normalizedMessageKeyMap.get(normalizeBackendMessage(fieldError));
    if (normalizedMatch) {
        return t(normalizedMatch);
    }

    return fieldError;
}

export function getFirstApiFieldError(error: unknown): string | null {
    return (
        Object.values(getApiFieldErrors(error))
            .flat()
            .find((value): value is string => Boolean(value)) ?? null
    );
}

export function resolveApiErrorMessage(
    error: unknown,
    t: (key: string, options?: Record<string, unknown>) => string,
    options: {
        fallbackKey: ApiErrorTranslationKey;
        statusFallbacks?: ApiErrorStatusFallbacks;
    }
): string {
    const translationKey = getApiErrorTranslationKey(error);

    if (translationKey) {
        return t(translationKey);
    }

    if (error instanceof ApiClientError) {
        const statusFallbackKey = options.statusFallbacks?.[error.status];
        if (statusFallbackKey) {
            return t(statusFallbackKey);
        }
    }

    return t(options.fallbackKey);
}
